import { useState, useEffect } from 'react';
import { FileImage, Clock, CheckCircle, XCircle } from 'lucide-react';
import api from '@/services/api';
import { Prescription } from '@/types';
import { PrescriptionUploader } from '@/components/common/PrescriptionUploader';
import { formatDateTime } from '@/utils';

const STATUS_CONFIG = {
  PENDING:  { icon: Clock,         color: 'text-yellow-500', bg: 'bg-yellow-50',  label: 'Pending Review' },
  VERIFIED: { icon: CheckCircle,   color: 'text-success',    bg: 'bg-green-50',   label: 'Verified' },
  REJECTED: { icon: XCircle,       color: 'text-error',      bg: 'bg-red-50',     label: 'Rejected' },
};

export const PrescriptionPage = () => {
  const [prescriptions, setPrescriptions] = useState<Prescription[]>([]);
  const [loading, setLoading] = useState(true);
  const [patientNotes, setPatientNotes] = useState('');

  const fetchPrescriptions = () => {
    api.get('/prescriptions').then(res => setPrescriptions(res.data.data || [])).finally(() => setLoading(false));
  };

  useEffect(() => { fetchPrescriptions(); }, []);

  const handleUploaded = (p: Prescription) => {
    setPrescriptions(prev => [p, ...prev]);
    setPatientNotes('');
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      <h1 className="font-heading font-bold text-2xl text-gray-800 mb-2">My Prescriptions</h1>
      <p className="text-gray-500 text-sm mb-8">Upload valid prescriptions for Schedule H/H1 medicines. Our pharmacist will review within 2 hours.</p>

      <div className="bg-white rounded-card shadow-card p-6 mb-8">
        <h2 className="font-heading font-semibold text-gray-800 mb-4">Upload New Prescription</h2>
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-600 mb-1">Note to pharmacist (optional)</label>
          <textarea
            value={patientNotes}
            onChange={e => setPatientNotes(e.target.value)}
            placeholder="e.g. Prescribed by Dr. Patel for diabetes management. Need 2 months supply."
            rows={2}
            className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 resize-none"
          />
        </div>
        <PrescriptionUploader onUploaded={handleUploaded} patientNotes={patientNotes} />
        <div className="mt-4 p-3 bg-blue-50 rounded-xl text-xs text-blue-700">
          <strong>Guidelines:</strong> Upload a clear photo of your prescription from a registered medical practitioner. Ensure date, doctor stamp, and medicines are clearly visible.
        </div>
      </div>

      <h2 className="font-heading font-semibold text-gray-800 mb-4">Uploaded Prescriptions ({prescriptions.length})</h2>
      {loading ? (
        <div className="space-y-3">{Array(3).fill(0).map((_, i) => <div key={i} className="h-24 bg-gray-100 rounded-xl animate-pulse" />)}</div>
      ) : prescriptions.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <FileImage className="h-12 w-12 mx-auto mb-3 opacity-40" />
          <p>No prescriptions uploaded yet.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {prescriptions.map(p => {
            const cfg = STATUS_CONFIG[p.status];
            const Icon = cfg.icon;
            return (
              <div key={p.id} className={`rounded-xl border-2 ${p.status === 'VERIFIED' ? 'border-green-200' : p.status === 'REJECTED' ? 'border-red-200' : 'border-gray-100'} bg-white overflow-hidden flex`}>
                <img src={p.imageUrl} alt="Prescription" className="w-32 h-28 object-cover shrink-0" />
                <div className="flex-1 p-4">
                  <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${cfg.bg} ${cfg.color} mb-2`}>
                    <Icon className="h-3.5 w-3.5" />
                    {cfg.label}
                  </div>
                  <p className="text-xs text-gray-400">{formatDateTime(p.createdAt)}</p>
                  {p.notes && (
                    <p className={`text-sm mt-2 ${p.status === 'REJECTED' ? 'text-error' : 'text-gray-600'}`}>
                      <strong>Pharmacist note:</strong> {p.notes}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
